import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Zap, ArrowRight, Loader, LayoutTemplate, MessageSquare, Briefcase, Video } from 'lucide-react';
import { getFlowTemplates } from 'Services/storeService';
import { message, Alert } from 'antd';
import AuthContext from 'context/AuthContext';
import IncompleteProfile from 'Pages/dashboard/Profile/IncompleteProfile';
import { STORE_ONBOARDING_STEP_CONFIG, getCompletedStepHashes } from 'utils/onboardingStepMapper';

const FlowSelection = () => {
  const { userProfile, onboardingStepCompletion } = useContext(AuthContext);
  const navigate = useNavigate();
  const { search } = useLocation();
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredId, setHoveredId] = useState(null);
  const fromOnboarding = new URLSearchParams(search).get('fromOnboarding') === 'true';
  const canAccessFlow = userProfile?.complete_profile || fromOnboarding;

  useEffect(() => {
    const fetchFlows = async () => {
      try {
        const response = await getFlowTemplates();
        setFlows(response.data?.data || []);
      } catch (error) {
        console.error("Failed to fetch flows:", error);
        message.error("Failed to load flow templates");
      } finally {
        setLoading(false);
      }
    };

    fetchFlows();
  }, []);

  const getCompletedOnboardingHashes = () => {
    const profileCompletedSteps = Array.isArray(userProfile?.onboarding_completed_steps)
      ? userProfile.onboarding_completed_steps
      : [];
    const completionMap = onboardingStepCompletion || {};
    const contextCompletedSteps = getCompletedStepHashes(completionMap, STORE_ONBOARDING_STEP_CONFIG);

    return new Set([...profileCompletedSteps, ...contextCompletedSteps]);
  };

  const getNextOnboardingRoute = () => {
    const completedSteps = getCompletedOnboardingHashes();
    const communicationsIndex = STORE_ONBOARDING_STEP_CONFIG.findIndex((step) => step.hash === 'communications');

    for (let index = communicationsIndex + 1; index < STORE_ONBOARDING_STEP_CONFIG.length; index += 1) {
      const nextStep = STORE_ONBOARDING_STEP_CONFIG[index];
      if (!completedSteps.has(nextStep.hash)) {
        return { pathname: '/user/completeprofile', hash: `#${nextStep.hash}` };
      }
    }

    return { pathname: '/user/welcome' };
  };

  const handleNextStep = () => {
    const nextRoute = getNextOnboardingRoute();
    navigate(nextRoute, { replace: true });
  };

  const handleSelectFlow = (flow) => {
    // Navigate to builder with flow ID in the URL query params (bookmarkable & shareable)
    const onboardingParam = fromOnboarding ? '&fromOnboarding=true' : '';
    navigate(`/user/flow/builder?flowId=${flow.id}${onboardingParam}`);
  };

  if (!canAccessFlow) {
    return <IncompleteProfile />;
  }

  const getIcon = (flow) => {
    const style = {
    padding: '10px 12px',
    borderRadius: '10px',
    display: 'inline-flex',
      marginBottom: '16px',
    };

    if (flow.name.includes('Demo')) {
      return (
        <div style={{ ...style, background: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6' }}>
          <Video size={24} />
        </div>
      );
    }
    if (flow.name.includes('Aggressive')) {
      return (
        <div style={{ ...style, background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' }}>
          <Zap size={24} />
        </div>
      );
    }
    if (flow.name.includes('SMS')) {
      return (
        <div style={{ ...style, background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>
          <MessageSquare size={24} />
        </div>
      );
    }
    if (flow.name.includes('Standard')) {
      return (
        <div style={{ ...style, background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' }}>
          <Briefcase size={24} />
        </div>
      );
    }
    
    return (
      <div style={{ ...style, background: 'rgba(99, 102, 241, 0.1)', color: '#6366f1' }}>
        <LayoutTemplate size={24} />
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh', 
        background: '#f8fafc',
        fontFamily: "'Inter', system-ui, sans-serif"
      }}>
        <div style={{ textAlign: 'center' }}>
          <Loader className="animate-spin" size={40} color="#6366f1" style={{ marginBottom: '16px' }} />
          <p style={{ color: '#64748b', fontSize: '14px', fontWeight: '500' }}>Loading templates...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: '#f8fafc', // Slate-50 like background
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      padding: '40px 24px'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header Section */}
        <div style={{ marginBottom: '40px', textAlign: 'center' }}>
          <h1 style={{ 
            fontSize: '30px', 
            fontWeight: '800', 
            color: '#0f172a', // Slate-900
            marginBottom: '16px',
            letterSpacing: '-0.02em',
            lineHeight: '1.2'
          }}>
            Select a Workflow
          </h1>
          <p style={{ 
            fontSize: '16px', 
            color: '#64748b', // Slate-500
            maxWidth: '600px',
            margin: '0 auto',
            lineHeight: '1.5'
          }}>
            Choose a professionally designed template to automate your communications. 
            Scale your operations with proven workflows.
          </p>
        </div>

        {fromOnboarding && (
          <div style={{ marginBottom: '24px' }}>
            <Alert
              type="warning"
              showIcon
              message="Please visit each flow at least once before continuing onboarding."
            />
          </div>
        )}

        {/* Grid Container */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', 
          gap: '24px',
          padding: '0'
        }}>
          {flows.map((flow) => (
             <div 
              key={flow.id}
              onClick={() => handleSelectFlow(flow)}
              onMouseEnter={() => setHoveredId(flow.id)}
              onMouseLeave={() => setHoveredId(null)}
              style={{
                background: 'white',
                borderRadius: '16px',
                padding: '24px',
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                border: hoveredId === flow.id ? '1px solid #6366f1' : '1px solid #e2e8f0',
                boxShadow: hoveredId === flow.id 
                  ? '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)' 
                  : '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
                transform: hoveredId === flow.id ? 'translateY(-4px)' : 'translateY(0)',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              {/* Decorative gradient blob */}
              <div style={{
                position: 'absolute',
                top: '-50px',
                right: '-50px',
                width: '150px',
                height: '150px',
                background: 'radial-gradient(circle, rgba(99, 102, 241, 0.05) 0%, rgba(255,255,255,0) 70%)',
                borderRadius: '50%',
                pointerEvents: 'none'
              }} />

              {/* Icon Container */}
              <div style={{ position: 'relative' }}>
                {getIcon(flow)}
              </div>
              
              <h3 style={{ 
                fontSize: '18px', 
                fontWeight: '700', 
                color: '#1e293b', 
                marginBottom: '8px',
                letterSpacing: '-0.01em'
              }}>
                {flow.name}
              </h3>
              
              <p style={{ 
                fontSize: '14px', 
                color: '#64748b', 
                lineHeight: '1.5', 
                marginBottom: '24px',
                flex: 1 
              }}>
                {flow.description}
              </p>
              
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                paddingTop: '20px',
                borderTop: '1px solid #f1f5f9',
                marginTop: 'auto'
              }}>
                <span style={{ 
                  fontSize: '13px', 
                  fontWeight: '600',  
                  color: hoveredId === flow.id ? '#6366f1' : '#475569',
                  transition: 'color 0.2s',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  Manage Communication
                </span>
                <span style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  background: hoveredId === flow.id ? '#6366f1' : '#f1f5f9',
                  color: hoveredId === flow.id ? 'white' : '#94a3b8',
                  transition: 'all 0.2s',
                  transform: hoveredId === flow.id ? 'translateX(4px)' : 'translateX(0)'
                }}>
                   <ArrowRight size={16} />
                </span>
              </div>
            </div>
          ))}
        </div>

        {fromOnboarding && (
          <div className="button-place text-end d-flex gap-2 justify-content-end" style={{ marginTop: '24px' }}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleNextStep}
              title="Continue to the next onboarding step"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FlowSelection;
